import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import BottomNav from '../components/BottomNav';
import MatchCard from '../components/MatchCard';
import ClubCard from '../components/ClubCard';
import NotificationCenter from '../components/NotificationCenter';
import OnlineFriends from '../components/OnlineFriends';
import LanguageSelector from '../components/LanguageSelector';
import RoleSelectionModal from '../components/RoleSelectionModal';
import GlobalSearch from '../components/GlobalSearch';
import MatchInvitationsSheet from '../components/MatchInvitationsSheet';
import GroupClassInvitationsSheet from '../components/GroupClassInvitationsSheet';
import ThemeToggle from '../components/ThemeToggle';
import { useWebSocket } from '../hooks/useWebSocket';
import { Trophy, Flame, TrendingUp, LogOut, MessageCircle, Sparkles, Swords, Users, Brain, Gift, Target, Activity, Zap, Search, Shield, BarChart3, Bell, RefreshCw, Building2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { getDisplayName } from '../utils/displayName';

const HomePage = () => {
  const { user, logout, needsProfileCompletion, completeProfile, checkAuth } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [userClub, setUserClub] = useState(null);
  
  // Use WebSocket for real-time live stats (replaces polling)
  const { liveStats } = useWebSocket();
  
  // Check if coming from Google OAuth with profile completion needed
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [googleUserName, setGoogleUserName] = useState('');

  useEffect(() => {
    // Check location state for new Google user needing profile completion
    if (location.state?.needsProfileCompletion) {
      setShowRoleModal(true);
      setGoogleUserName(location.state.user?.name || '');
      // Clear the state to prevent modal showing again on refresh
      window.history.replaceState({}, document.title);
    } else if (needsProfileCompletion) {
      setShowRoleModal(true);
      setGoogleUserName(user?.name || '');
    }
  }, [location.state, needsProfileCompletion, user]);

  useEffect(() => {
    loadData();
  }, []);

  // Load user's club if they are a club owner
  useEffect(() => {
    const loadUserClub = async () => {
      if (user?.role === 'club_owner' && user?.id) {
        try {
          const { data } = await api.get(`/clubs/my`, { withCredentials: true });
          if (data) {
            setUserClub(data);
          }
        } catch (error) {
          // User might not have created a club yet
        }
      }
    };
    loadUserClub();
  }, [user]);

  const loadData = async () => {
    try {
      const [matchesRes, clubsRes, myMatchesRes] = await Promise.all([
        api.get(`/matches?status=open`, { withCredentials: true }),
        api.get(`/clubs`, { withCredentials: true }),
        api.get(`/matches/my-upcoming`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setMatches(matchesRes.data.slice(0, 3));
      setClubs(clubsRes.data.slice(0, 3));
      
      // Filter my matches to show only upcoming ones (not in the open matches list)
      const myUpcoming = myMatchesRes.data || [];
      setMyMatches(myUpcoming.slice(0, 3));
    } catch (error) {
      toast.error(t('errors.loadingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileComplete = (userData) => {
    completeProfile(userData);
    setShowRoleModal(false);
    // Refresh user data
    checkAuth();
    
    // Redirect club owners to club registration
    if (userData.role === 'club_owner') {
      navigate('/club/register');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('auth.logoutSuccess'));
      navigate('/login');
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <div className="animate-pulse theme-accent text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg pb-24 transition-colors duration-300 w-full overflow-x-hidden">
      {/* Role Selection Modal for new Google users */}
      <RoleSelectionModal 
        isOpen={showRoleModal} 
        onComplete={handleProfileComplete}
        userName={googleUserName}
      />
      
      {/* Hero Section - Compact with Logo */}
      <div className={`relative w-full ${
        isDark 
          ? 'bg-gradient-to-br from-[#0d1829] via-[#0a1420] to-[#0d1829]' 
          : 'bg-gradient-to-br from-[#e0f2fe] via-[#f0f9ff] to-[#e0f2fe]'
      }`}>
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-[radial-gradient(ellipse_at_top_right,rgba(0,212,255,0.15),transparent_50%)]'
            : 'bg-[radial-gradient(ellipse_at_top_right,rgba(8,145,178,0.1),transparent_50%)]'
        }`}></div>
        <div className="relative z-10 px-4 pt-2 pb-3">
          {/* Top row: Icons */}
          <div className="flex items-center justify-end gap-1 mb-3">
            <ThemeToggle size="small" />
            <LanguageSelector />
            {/* Club Owner: Manage My Club button */}
            {user?.role === 'club_owner' && userClub && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/club-admin/${userClub.id}`)}
                data-testid="manage-club-button"
                className="text-[#00d4ff] hover:text-[#00d4ff]/80 h-8 w-8"
                title={language === 'fr' ? 'Gérer mon club' : 'Manage my club'}
              >
                <Building2 size={18} />
              </Button>
            )}
            {/* Club Owner without club: Register club button */}
            {user?.role === 'club_owner' && !userClub && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/club/register')}
                data-testid="register-club-button"
                className="text-[#f59e0b] hover:text-[#f59e0b]/80 h-8 w-8 animate-pulse"
                title={language === 'fr' ? 'Créer mon club' : 'Create my club'}
              >
                <Building2 size={18} />
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                data-testid="admin-button"
                className="text-[#9333EA] hover:text-[#9333EA]/80 h-8 w-8"
                title={language === 'fr' ? 'Administration' : 'Admin Dashboard'}
              >
                <Shield size={18} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSearch(true)}
              data-testid="global-search-button"
              className={`h-8 w-8 ${isDark ? 'text-white hover:text-[#00d4ff]' : 'text-gray-700 hover:text-[#0891b2]'}`}
            >
              <Search size={18} />
            </Button>
            <OnlineFriends />
            <MatchInvitationsSheet />
            <GroupClassInvitationsSheet />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/messages')}
              data-testid="messages-button"
              className={`h-8 w-8 relative ${isDark ? 'text-white hover:text-[#00d4ff]' : 'text-gray-700 hover:text-[#0891b2]'}`}
            >
              <MessageCircle size={18} />
            </Button>
            <NotificationCenter />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className={`h-8 w-8 ${isDark ? 'text-white hover:text-[#00d4ff]' : 'text-gray-700 hover:text-[#0891b2]'}`}
            >
              <LogOut size={18} />
            </Button>
          </div>
          
          {/* Bottom row: Logo and Name */}
          <div className="flex items-center gap-3">
            <img 
              src="/icons/icon-72x72.png" 
              alt="XO Club" 
              className="w-12 h-12 rounded-xl neon-glow"
            />
            <div>
              <h1 className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('home.hello')}
              </h1>
              <p className={`text-lg font-bold ${isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'}`}>
                {getDisplayName(user)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-6">
        {/* Live Stats Banner */}
        {liveStats && (
          <div 
            data-testid="live-stats-banner"
            className={`rounded-xl p-4 border transition-colors duration-300 ${
              isDark 
                ? 'bg-gradient-to-r from-[#00d4ff]/10 via-[#9333EA]/10 to-[#00d4ff]/10 border-[#00d4ff]/30'
                : 'bg-white border-[#0891b2]/30 shadow-sm'
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Activity size={18} className={`animate-pulse ${isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'}`} />
              <span className={`text-sm font-bold uppercase tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'fr' ? 'En direct' : 'Live Now'}
              </span>
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Users size={14} className="text-green-500" />
                  <span className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{liveStats.playersOnline}</span>
                </div>
                <div className={`text-[10px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'En ligne' : 'Online'}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={14} className="text-yellow-500" />
                  <span className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{liveStats.openMatches}</span>
                </div>
                <div className={`text-[10px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Matchs' : 'Matches'}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Trophy size={14} className="text-orange-500" />
                  <span className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{liveStats.activeTournaments}</span>
                </div>
                <div className={`text-[10px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Tournois' : 'Tourneys'}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <TrendingUp size={14} className={isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'} />
                  <span className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{liveStats.matchesToday}</span>
                </div>
                <div className={`text-[10px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? "Aujourd'hui" : 'Today'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div
            data-testid="stat-card-rating"
            className={`rounded-xl p-4 text-center transition-colors duration-300 border ${
              isDark ? 'bg-[#0f1c2e] border-[#1e3a5f]' : 'bg-white border-gray-200 shadow-sm'
            }`}
          >
            <Trophy size={24} className={`mx-auto mb-2 ${isDark ? 'text-[#c8e600]' : 'text-[#65a30d]'}`} />
            <div className={`text-2xl font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.rating?.displayRating?.toFixed(0) || 0}
            </div>
            <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{t('home.rating')}</div>
          </div>

          <div
            data-testid="stat-card-matches"
            className={`rounded-xl p-4 text-center transition-colors duration-300 border ${
              isDark ? 'bg-[#0f1c2e] border-[#1e3a5f]' : 'bg-white border-gray-200 shadow-sm'
            }`}
          >
            <TrendingUp size={24} className={`mx-auto mb-2 ${isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'}`} />
            <div className={`text-2xl font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.stats?.matchesPlayed || 0}
            </div>
            <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{t('home.matches')}</div>
          </div>

          <div
            data-testid="stat-card-streak"
            className={`rounded-xl p-4 text-center transition-colors duration-300 border ${
              isDark ? 'bg-[#0f1c2e] border-[#1e3a5f]' : 'bg-white border-gray-200 shadow-sm'
            }`}
          >
            <Flame
              size={24}
              className={`mx-auto mb-2 ${
                (user?.stats?.currentStreak || 0) >= 3 ? 'text-orange-500' : (isDark ? 'text-zinc-500' : 'text-gray-400')
              }`}
            />
            <div className={`text-2xl font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.stats?.currentStreak || 0}
            </div>
            <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{t('home.streak')}</div>
          </div>
        </div>

        {/* Stats Button */}
        <button
          onClick={() => navigate('/stats')}
          data-testid="view-stats-btn"
          className={`w-full rounded-xl p-4 text-center font-bold transition-all border ${
            isDark 
              ? 'bg-gradient-to-r from-[#00d4ff]/10 to-purple-500/10 border-[#00d4ff]/30 text-[#00d4ff] hover:border-[#00d4ff]' 
              : 'bg-gradient-to-r from-[#0891b2]/10 to-purple-500/10 border-[#0891b2]/30 text-[#0891b2] hover:border-[#0891b2]'
          }`}
        >
          <BarChart3 size={20} className="inline-block mr-2" />
          {language === 'fr' ? 'VOIR MES STATISTIQUES DÉTAILLÉES' : 'VIEW MY DETAILED STATISTICS'}
        </button>

        {/* Quick Actions - New Features */}
        <div>
          <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Actions rapides' : 'Quick Actions'}</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/groups')}
              data-testid="quick-groups-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-[#00d4ff]/20 to-[#00d4ff]/5 border-[#00d4ff]/30 hover:border-[#00d4ff]/60'
                  : 'bg-gradient-to-br from-[#0891b2]/15 to-[#0891b2]/5 border-[#0891b2]/30 hover:border-[#0891b2]/60 shadow-sm'
              }`}
            >
              <Users size={28} className={`mb-2 ${isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'}`} />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Mes Groupes' : 'My Groups'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Organiser des matchs' : 'Organize matches'}</p>
            </button>

            <button
              onClick={() => navigate('/invite')}
              data-testid="quick-invite-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/5 border-pink-500/30 hover:border-pink-500/60'
                  : 'bg-gradient-to-br from-pink-500/15 to-purple-500/5 border-pink-500/30 hover:border-pink-500/60 shadow-sm'
              }`}
            >
              <Gift size={28} className="text-pink-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Inviter' : 'Invite'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Parrainer un ami' : 'Refer a friend'}</p>
            </button>

            <button
              onClick={() => navigate('/feed')}
              data-testid="quick-feed-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-red-500/20 to-orange-500/5 border-red-500/30 hover:border-red-500/60'
                  : 'bg-gradient-to-br from-red-500/15 to-orange-500/5 border-red-500/30 hover:border-red-500/60 shadow-sm'
              }`}
            >
              <Activity size={28} className="text-red-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Activités' : 'Feed'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Voir mes amis' : 'See friends'}</p>
            </button>

            <button
              onClick={() => navigate('/coach')}
              data-testid="quick-coach-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-[#9333EA]/20 to-[#9333EA]/5 border-[#9333EA]/30 hover:border-[#9333EA]/60'
                  : 'bg-gradient-to-br from-[#9333EA]/15 to-[#9333EA]/5 border-[#9333EA]/30 hover:border-[#9333EA]/60 shadow-sm'
              }`}
            >
              <Brain size={28} className="text-[#9333EA] mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Coach IA' : 'AI Coach'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Analyse personnalisée' : 'Personal analysis'}</p>
            </button>

            <button
              onClick={() => navigate('/challenges')}
              data-testid="quick-challenges-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-red-500/20 to-orange-500/5 border-red-500/30 hover:border-red-500/60'
                  : 'bg-gradient-to-br from-red-500/15 to-orange-500/5 border-red-500/30 hover:border-red-500/60 shadow-sm'
              }`}
            >
              <Swords size={28} className="text-red-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Défis' : 'Challenges'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Défier un joueur' : 'Challenge a player'}</p>
            </button>

            <button
              onClick={() => navigate('/partners')}
              data-testid="quick-partners-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30 hover:border-green-500/60'
                  : 'bg-gradient-to-br from-green-500/15 to-green-500/5 border-green-500/30 hover:border-green-500/60 shadow-sm'
              }`}
            >
              <Users size={28} className="text-green-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Partenaires' : 'Partners'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Trouver un partenaire' : 'Find a partner'}</p>
            </button>

            <button
              onClick={() => navigate('/group-classes')}
              data-testid="quick-group-classes-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/60'
                  : 'bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/60 shadow-sm'
              }`}
            >
              <GraduationCap size={28} className="text-emerald-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Cours' : 'Classes'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Cours collectifs' : 'Group classes'}</p>
            </button>

            <button
              onClick={() => navigate('/tournaments')}
              data-testid="quick-tournaments-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border-yellow-500/30 hover:border-yellow-500/60'
                  : 'bg-gradient-to-br from-yellow-500/15 to-yellow-500/5 border-yellow-500/30 hover:border-yellow-500/60 shadow-sm'
              }`}
            >
              <Trophy size={28} className="text-yellow-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Tournois' : 'Tournaments'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Compétitions' : 'Competitions'}</p>
            </button>

            <button
              onClick={() => navigate('/waitlist')}
              data-testid="quick-waitlist-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-orange-500/20 to-orange-500/5 border-orange-500/30 hover:border-orange-500/60'
                  : 'bg-gradient-to-br from-orange-500/15 to-orange-500/5 border-orange-500/30 hover:border-orange-500/60 shadow-sm'
              }`}
            >
              <Bell size={28} className="text-orange-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Liste d\'attente' : 'Waitlist'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Créneaux libérés' : 'Freed slots'}</p>
            </button>

            <button
              onClick={() => navigate('/recurring-bookings')}
              data-testid="quick-recurring-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-indigo-500/20 to-indigo-500/5 border-indigo-500/30 hover:border-indigo-500/60'
                  : 'bg-gradient-to-br from-indigo-500/15 to-indigo-500/5 border-indigo-500/30 hover:border-indigo-500/60 shadow-sm'
              }`}
            >
              <RefreshCw size={28} className="text-indigo-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Récurrent' : 'Recurring'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Réservation auto' : 'Auto booking'}</p>
            </button>

            <button
              onClick={() => navigate('/ai-coaching')}
              data-testid="quick-ai-coaching-btn"
              className={`rounded-xl p-4 text-left transition-all border ${
                isDark 
                  ? 'bg-gradient-to-br from-pink-500/20 to-pink-500/5 border-pink-500/30 hover:border-pink-500/60'
                  : 'bg-gradient-to-br from-pink-500/15 to-pink-500/5 border-pink-500/30 hover:border-pink-500/60 shadow-sm'
              }`}
            >
              <Brain size={28} className="text-pink-500 mb-2" />
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Coach IA' : 'AI Coach'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Conseils perso' : 'Personal tips'}</p>
            </button>
          </div>

          {/* Daily Rewards Banner */}
          <button
            onClick={() => navigate('/daily-rewards')}
            data-testid="daily-rewards-btn"
            className={`w-full rounded-xl p-4 flex items-center gap-4 transition-all border ${
              isDark 
                ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30 hover:border-purple-500/60'
                : 'bg-gradient-to-r from-purple-500/15 to-pink-500/15 border-purple-500/30 hover:border-purple-500/60 shadow-sm'
            }`}
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Gift size={24} className="text-white fill-current" />
            </div>
            <div className="flex-1 text-left">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Récompenses quotidiennes' : 'Daily Rewards'}</h3>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{language === 'fr' ? 'Réclamez votre bonus du jour !' : 'Claim your daily bonus!'}</p>
            </div>
            <Sparkles size={24} className="text-yellow-400 fill-current animate-pulse" />
          </button>

          {/* Weekly Missions Banner */}
          <button
            onClick={() => navigate('/missions')}
            data-testid="weekly-missions-btn"
            className="w-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-4 flex items-center gap-4 hover:border-blue-500/60 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <Target size={24} className="theme-text fill-current" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-bold theme-text">{language === 'fr' ? 'Missions hebdomadaires' : 'Weekly Missions'}</h3>
              <p className="text-xs text-zinc-400">{language === 'fr' ? 'Gagnez des XP supplémentaires !' : 'Earn extra XP!'}</p>
            </div>
            <Flame size={24} className="text-orange-400 fill-current" />
          </button>
        </div>

        {/* My Upcoming Matches - Only show if user has matches */}
        {myMatches.length > 0 && (
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-[#00d4ff]/10 via-transparent to-[#00d4ff]/10 rounded-2xl blur-xl" />
            <div className={`relative rounded-2xl p-4 border-2 ${
              isDark 
                ? 'bg-[#0a1628]/80 border-[#00d4ff]/30' 
                : 'bg-white/80 border-[#0891b2]/30'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${isDark ? 'bg-[#00d4ff]/20' : 'bg-[#0891b2]/20'}`}>
                    <Zap size={20} className={isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'} />
                  </div>
                  <h2 className="text-xl font-bold theme-text">
                    {language === 'fr' ? 'Mes prochains matchs' : 'My upcoming matches'}
                  </h2>
                </div>
                <button
                  onClick={() => navigate('/matches?filter=my')}
                  className="theme-accent text-sm font-medium hover:underline"
                >
                  {t('common.viewAll')}
                </button>
              </div>
              <div className="space-y-4">
                {myMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Open Matches */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold theme-text">{t('home.openMatches')}</h2>
            <button
              onClick={() => navigate('/matches')}
              data-testid="view-all-matches"
              className="theme-accent text-sm font-medium hover:underline"
            >
              {t('common.viewAll')}
            </button>
          </div>
          {matches.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              {t('home.noMatches')}
            </div>
          ) : (
            <div className="space-y-4">
              {matches.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          )}
        </div>

        {/* Clubs */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold theme-text">{t('home.partnerClubs')}</h2>
            <button
              onClick={() => navigate('/clubs')}
              data-testid="view-all-clubs"
              className="theme-accent text-sm font-medium hover:underline"
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {clubs.map((club) => (
              <ClubCard key={club.id} club={club} />
            ))}
          </div>
        </div>

        {/* Quick Links: Leagues & Rankings */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/leagues')}
            className="bg-gradient-to-br from-[#FFD700]/20 to-[#FFD700]/5 border border-[#FFD700]/30 rounded-xl p-4 text-left hover:border-[#FFD700]/50 transition-all"
            data-testid="leagues-link"
          >
            <div className="text-3xl mb-2">👑</div>
            <h3 className="text-lg font-bold theme-text">{language === 'fr' ? 'Ligues' : 'Leagues'}</h3>
            <p className="text-xs text-zinc-400">{language === 'fr' ? 'Compétitions saisonnières' : 'Seasonal competitions'}</p>
          </button>
          <button
            onClick={() => navigate('/ranking')}
            className="bg-gradient-to-br from-[#00d4ff]/20 to-[#00d4ff]/5 border border-[#00d4ff]/30 rounded-xl p-4 text-left hover:border-[#00d4ff]/50 transition-all"
            data-testid="ranking-link"
          >
            <div className="text-3xl mb-2">📊</div>
            <h3 className="text-lg font-bold theme-text">{language === 'fr' ? 'Classement' : 'Rankings'}</h3>
            <p className="text-xs text-zinc-400">{language === 'fr' ? 'Top joueurs' : 'Top players'}</p>
          </button>
        </div>
      </div>

      {/* Floating AI Assistant Button */}
      <button
        onClick={() => navigate('/ai-assistant')}
        data-testid="ai-assistant-fab"
        className="fixed bottom-24 right-4 w-14 h-14 bg-gradient-to-r from-[#00d4ff] to-[#9333EA] rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center z-40"
        title={language === 'fr' ? 'Assistant IA' : 'AI Assistant'}
      >
        <Sparkles size={24} className="theme-text fill-current" />
      </button>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />

      <BottomNav />
    </div>
  );
};

export default HomePage;
